import { useState } from 'react';
import type { UploadFile } from 'antd';
import type { AxiosProgressEvent } from 'axios';
import type { ApiImportData } from '@dc-trans/data-layer';

interface UseFileValidationProps {
  maxSize: number; // in MB
  maxCount: number;
  multiple: boolean;
}

interface UseFileValidationReturn {
  fileList: UploadFile[];
  uploadError: string;
  validateAndSetFiles: (_files: UploadFile[]) => void;
  progressCallback: (_progressEvent: AxiosProgressEvent) => void;
  removeFile: (_file: UploadFile) => void;
  clearFiles: () => void;
  clearError: () => void;
  hasFiles: boolean;
  uploadResult: ApiImportData | null;
  setUploadResult: (_result: ApiImportData | null) => void;
  setUploadError: (_error: string) => void;
}

export const useFileValidation = ({ maxSize, maxCount, multiple }: UseFileValidationProps): UseFileValidationReturn => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadResult, setUploadResult] = useState<ApiImportData | null>(null);
  const [uploadError, setUploadError] = useState<string>('');

  const validateAndSetFiles = (files: UploadFile[]) => {
    setUploadError('');
    setUploadResult(null); // Clear previous upload results

    // Validate file size and file object
    const validFiles = files.filter((file: UploadFile) => {
      // Check if file has valid originFileObj
      if (!file.originFileObj) {
        setUploadError(`File "${file.name}" is invalid or corrupted`);
        return false;
      }

      const fileSizeMB = file.originFileObj.size / (1024 * 1024);
      if (fileSizeMB > maxSize) {
        setUploadError(`File "${file.name}" exceeds maximum size of ${maxSize}MB`);
        return false; // Exclude invalid files from the list
      }

      return true;
    });

    setFileList(currentFileList => {
      if (multiple) {
        // For multiple files, append new files to existing ones
        const combinedFiles = [...currentFileList, ...validFiles];
        // Remove duplicates based on uid and limit to maxCount
        const uniqueFiles = combinedFiles.filter(
          (file, index, arr) => arr.findIndex(f => f.uid === file.uid) === index
        );
        return uniqueFiles.slice(0, maxCount);
      } else {
        // For single file, replace with the latest valid file
        return validFiles.slice(-1);
      }
    });
  };

  const removeFile = (file: UploadFile) => {
    setFileList(prev => prev.filter(f => f.uid !== file.uid));
    setUploadError('');
  };

  const clearFiles = () => {
    setFileList([]);
    setUploadError('');
  };

  const clearError = () => {
    setUploadError('');
  };

  const progressCallback = (progressEvent: AxiosProgressEvent) => {
    const percent = Math.floor(progressEvent.progress! * 100);

    setFileList(currentFileList => {
      if (currentFileList.length === 0) return currentFileList;

      const resFiles = [...currentFileList];
      resFiles[0].percent = percent;
      if (percent >= 100) {
        resFiles[0].status = 'done';
      } else {
        resFiles[0].status = 'uploading';
      }
      return resFiles;
    });
  };

  const hasFiles = fileList.length > 0;

  return {
    fileList,
    uploadError,
    uploadResult,
    setUploadResult,
    validateAndSetFiles,
    progressCallback,
    removeFile,
    clearFiles,
    clearError,
    setUploadError,
    hasFiles,
  };
};
